import { memo, useCallback, useEffect, useMemo, useState } from "react";

// Helpers
import { useTranslate } from "helpers";

// UI
import { Dialog } from "ui/components/Dialog";
import { Modal } from "ui/components/Modal";
import TransferIntro from "./components/TransferIntro/TransferIntro";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { Wizard } from "ui/components/Wizard";
import WizardStep from "ui/components/Wizard/WizardStep";
import WizardFooter from "ui/components/Wizard/WizardFooter";
import VehicleTransferForm from "./components/VehicleTransferForm/VehicleTransferForm";
import VehicleTransferDetailsForm from "./components/VehicleTransferDetailsForm/VehicleTransferDetailsForm";
import VehicleTransferExpensesForm from "./components/VehicleTransferExpensesForm/VehicleTransferExpensesForm";
import VehicleStatusFlow from "ui/modules/fleet/vehicles/pages/components/VehicleStatusEditCard/components/VehicleStatusFlow/VehicleStatusFlow";
import VehicleServiceEditCard from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleServiceEditCard/VehicleServiceEditCard";

// Servisni sloj
import {
  transferVehicleAPI,
  updateVehicleTransferAPI,
} from "services/api/fleet/vehicles/locations/api";
import { setFleetVehicleExpenseLocationAPI } from "services/api/fleet/vehicles/expenses/api";
import { FleetVehicleExpense } from "services/api/fleet/vehicles/expenses/types";
import { FLEET_VEHICLE_STATUS_CONFIG } from "services/api/fleet/vehicles/consts";
import { FleetVehicleStatusEnum } from "services/api/fleet/vehicles/types";
import {
  getSuggestedStatus,
  initialVehicleLocationForm,
} from "services/api/fleet/vehicles/locations/consts";
import {
  VehicleLocation,
  VehicleLocationFormValues,
  VehicleLocationTypeEnum,
} from "services/api/fleet/vehicles/locations/types";

// Validacija
import { Errors } from "services/forms/validation/types";
import { clearChangedErrors, validateYup } from "services/forms/validation/validation";
import { VEHICLE_LOCATION_SCHEMA } from "ui/modules/fleet/vehicles/pages/components/VehicleLocationFields/validation";
import { VEHICLE_TRANSFER_SCHEMA } from "./validation";

import { VehicleTransferEditCardProps } from "./types";

/**
 * Premještaj vozila. Ne prepisuje lokaciju nego zatvara zatečenu i otvara novu,
 * pa se kasnije zna gdje je vozilo kada bilo.
 *
 * Status se ne mijenja usput: kad nova lokacija podrazumijeva drugi status (servis,
 * popravak štete, dolazak, povratak u poslovnicu), nakon spremanja se ponudi promjena —
 * isto kao nakon unosa štete ili servisnog naloga. Odlazak na servis se ondje zaključuje
 * nalogom, jer vozilo ne smije biti "na servisu" bez njega.
 */
const VehicleTransferEditCard: React.FC<VehicleTransferEditCardProps> = (props) => {
  const {
    show,
    vehicleId,
    currentLocation,
    currentStatus,
    transfer,
    onClose,
    onSuccess,
    onStatusChange,
  } = props;

  const { t } = useTranslate();

  const [formValues, setFormValues] = useState<VehicleLocationFormValues>(
    initialVehicleLocationForm
  );
  const [errors, setErrors] = useState<Errors>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [stepCurrent, setStepCurrent] = useState<number>(0);
  const [expenses, setExpenses] = useState<FleetVehicleExpense[]>([]);
  // Ponuda promjene statusa nakon spremljenog premještaja, pa ponuda servisnog naloga.
  const [showStatusDialog, setShowStatusDialog] = useState<boolean>(false);
  const [showStatusFlow, setShowStatusFlow] = useState<boolean>(false);
  const [showServiceDialog, setShowServiceDialog] = useState<boolean>(false);
  const [showServiceWizard, setShowServiceWizard] = useState<boolean>(false);

  /** Ispravak već zapisanog premještaja, a ne novi premještaj. */
  const isEdit = !!transfer?.id;

  // Ispravak ne mijenja status: status je zabilježen u trenutku kad se premještaj dogodio,
  // a ovdje se popravlja zapis o tome što je tada bilo.
  const suggestedStatus = useMemo(
    () =>
      isEdit ? null : getSuggestedStatus(formValues.locationType, currentStatus ?? null),
    [isEdit, formValues.locationType, currentStatus]
  );

  // Otvara se popunjen: kod ispravka premještajem koji se mijenja, inače zatečenom
  // lokacijom — da se mijenja samo ono što se stvarno mijenja.
  useEffect(() => {
    if (!show) return;

    setErrors({});
    setStepCurrent(0);
    setExpenses([]);
    setShowStatusDialog(false);
    setShowStatusFlow(false);
    setShowServiceDialog(false);
    setShowServiceWizard(false);

    const source = transfer ?? currentLocation;

    setFormValues({
      ...initialVehicleLocationForm,
      locationType:
        (source?.locationType as VehicleLocationTypeEnum) ??
        initialVehicleLocationForm.locationType,
      officeId: source?.officeId ?? null,
      contactId: source?.contactId ?? null,
      addressLine1: source?.addressLine1 ?? null,
      addressCity: source?.addressCity ?? null,
      addressPostalCode: source?.addressPostalCode ?? null,
      addressCountry: source?.addressCountry ?? null,
      // Naziv, napomena i vozač pripadaju premještaju, ne lokaciji — kod novog se ne
      // preuzimaju od zatečene lokacije.
      name: transfer?.name ?? null,
      note: transfer?.note ?? null,
      driverId: transfer?.driverId ?? null,
      // Premještaj se u pravilu unosi kad se dogodi — datum se mijenja samo kod
      // naknadnog unosa.
      validFrom: transfer?.validFrom ? new Date(transfer.validFrom) : new Date(),
    });
  }, [show, currentLocation, transfer]);

  const handleFieldsChange = useCallback(
    (values: VehicleLocationFormValues) => {
      clearChangedErrors(formValues, values, setErrors);
      setFormValues(values);
    },
    [formValues]
  );

  const saveTransfer = useCallback(async (): Promise<VehicleLocation | null> => {
    if (isEdit) {
      return await updateVehicleTransferAPI(vehicleId, transfer!.id!, formValues, t);
    }

    return await transferVehicleAPI(vehicleId, formValues, t);
  }, [isEdit, transfer, vehicleId, formValues, t]);

  const handleFinish = useCallback(async () => {
    const isValid = validateYup(formValues, VEHICLE_TRANSFER_SCHEMA, setErrors);
    if (!isValid) return;

    setIsSaving(true);
    try {
      const saved = await saveTransfer();

      // Troškovi su spremljeni kroz svoj modal prije nego je premještaj postojao,
      // pa im se veza dodaje sada.
      for (const expense of expenses) {
        if (saved?.id) await setFleetVehicleExpenseLocationAPI(expense.id, saved.id);
      }

      if (saved) onSuccess(saved);
      onClose();

      // Nova lokacija ne mora značiti i drugo stanje vozila (npr. vozilo koje ostaje u
      // prodaji dok čeka termin), pa se promjena statusa nudi, a ne radi sama od sebe.
      if (suggestedStatus) setShowStatusDialog(true);
    } catch (err) {
      // Greška ide kroz API interceptor; modal ostaje otvoren da se unos ne izgubi.
      console.error("Greška prilikom premještaja vozila:", err);
    } finally {
      setIsSaving(false);
    }
  }, [formValues, saveTransfer, expenses, onSuccess, onClose, suggestedStatus]);

  const handleStatusDialogConfirm = useCallback(() => {
    setShowStatusDialog(false);
    setShowStatusFlow(true);
  }, []);

  const handleStatusDialogCancel = useCallback(() => {
    setShowStatusDialog(false);
  }, []);

  const handleStatusFlowClose = useCallback(() => {
    setShowStatusFlow(false);
  }, []);

  const handleStatusSaved = useCallback(
    (status: FleetVehicleStatusEnum) => {
      onStatusChange?.(status);

      // Nalog nije uvjet za status — na njega se vežu troškovi i štete popravka, pa se
      // nudi zasebno umjesto da se otvori sam od sebe.
      if (status === FleetVehicleStatusEnum.UNAVAILABLE_SERVICE) {
        setShowServiceDialog(true);
      }
    },
    [onStatusChange]
  );

  const handleServiceDialogConfirm = useCallback(() => {
    setShowServiceDialog(false);
    setShowServiceWizard(true);
  }, []);

  const handleServiceDialogCancel = useCallback(() => {
    setShowServiceDialog(false);
  }, []);

  const handleServiceWizardClose = useCallback(() => {
    setShowServiceWizard(false);
  }, []);

  // Svaki korak propušta dalje tek kad su njegova polja ispravna; troškovi su neobavezni.
  const handleNextStep = useCallback(() => {
    if (stepCurrent === 0) {
      const isValid = validateYup(formValues, VEHICLE_LOCATION_SCHEMA, setErrors);
      if (!isValid) return;
    }

    if (stepCurrent === 1) {
      const isValid = validateYup(formValues, VEHICLE_TRANSFER_SCHEMA, setErrors);
      if (!isValid) return;
    }

    setStepCurrent((step) => step + 1);
  }, [stepCurrent, formValues]);

  const handleBackStep = useCallback(() => {
    setStepCurrent((step) => step - 1);
  }, []);

  const steps = useMemo(
    () => [
      <WizardStep key="location" stepNumber={0} stepCurrent={stepCurrent}>
        <VehicleTransferForm
          values={formValues}
          errors={errors}
          onChange={handleFieldsChange}
          currentLocation={currentLocation}
        />
      </WizardStep>,
      <WizardStep key="details" stepNumber={1} stepCurrent={stepCurrent}>
        <VehicleTransferDetailsForm
          values={formValues}
          errors={errors}
          onChange={handleFieldsChange}
          vehicleId={vehicleId}
        />
      </WizardStep>,
      // Troškovi se dodaju uz novi premještaj. Kod ispravka bi korak bio prazan i kad
      // troškovi postoje — vežu se na tabu Troškovi, a ne ovdje.
      ...(isEdit
        ? []
        : [
            <WizardStep key="expenses" stepNumber={2} stepCurrent={stepCurrent}>
              <VehicleTransferExpensesForm
                vehicleId={vehicleId}
                expenses={expenses}
                onChange={setExpenses}
              />
            </WizardStep>,
          ]),
    ],
    [
      stepCurrent,
      formValues,
      errors,
      handleFieldsChange,
      currentLocation,
      expenses,
      vehicleId,
      isEdit,
    ]
  );

  // Tok promjene statusa ide preko cijelog ekrana i dijeli razinu s ovim modalom; do tada
  // je premještaj ionako spremljen i zatvoren.
  const showTransfer = show && !showStatusFlow;

  return (
    <>
      <Modal
        show={showTransfer}
        onHide={onClose}
        size="full"
        position="side"
        intro={{
          content: (
            <TransferIntro
              formValues={formValues}
              currentLocation={currentLocation ?? null}
              expenses={expenses}
              isEdit={isEdit}
              stepCurrent={stepCurrent}
              stepCount={steps.length}
            />
          ),
        }}
      >
        <ModalHeader
          icon="/media/icons/location.svg"
          title={isEdit ? "Ažuriraj premještaj" : "Premjesti vozilo"}
          onHide={onClose}
        />
        <ModalBody>
          <Wizard steps={steps} />
        </ModalBody>
        <WizardFooter
          stepCurrent={stepCurrent}
          steps={steps}
          onNextStep={handleNextStep}
          onBackStep={handleBackStep}
          onFinish={handleFinish}
          isLoading={isSaving}
          isReadOnly={false}
        />
      </Modal>

      <Dialog
        open={showStatusDialog}
        title="Promijeniti status vozila?"
        message={`Premještaj je spremljen. Prema novoj lokaciji status se može odmah promijeniti u „${t(
          FLEET_VEHICLE_STATUS_CONFIG[suggestedStatus ?? FleetVehicleStatusEnum.AVAILABLE]?.label ?? ""
        )}”.`}
        confirmText="Promijeni status"
        cancelText="Ne, hvala"
        onConfirm={handleStatusDialogConfirm}
        onCancel={handleStatusDialogCancel}
      />

      {/* Tok kreće odmah na predloženom statusu, s podacima premještaja — što premještaj
          već zna, ne pita se ponovno. Nedostaje li nešto (npr. šteta), tok pita. */}
      <VehicleStatusFlow
        show={showStatusFlow}
        vehicleId={vehicleId}
        currentStatus={currentStatus ?? FleetVehicleStatusEnum.AVAILABLE}
        status={showStatusFlow ? suggestedStatus : null}
        prefill={{
          validFrom: formValues.validFrom,
          note: formValues.note,
          contactId: formValues.contactId,
        }}
        skipLocation
        skipServiceOrder
        autoSave
        onClose={handleStatusFlowClose}
        onSaved={handleStatusSaved}
      />

      <Dialog
        open={showServiceDialog}
        title="Otvoriti servisni nalog?"
        message="Vozilo je na servisu. Troškovi i štete vežu se na nalog, pa se poslije zna koliko je popravak koštao."
        confirmText="Otvori nalog"
        cancelText="Kasnije"
        onConfirm={handleServiceDialogConfirm}
        onCancel={handleServiceDialogCancel}
      />

      {/* Status je već upisan, pa nalog ide svojim endpointom. Vozilo je premješteno,
          pa nalog ne dira lokaciju. */}
      {showServiceWizard && (
        <VehicleServiceEditCard
          show={showServiceWizard}
          vehicleId={vehicleId}
          initialLocation={null}
          initialValues={{
            contactId: formValues.contactId,
            receivedDate: formValues.validFrom,
            description: formValues.note,
          }}
          onClose={handleServiceWizardClose}
        />
      )}
    </>
  );
};

export default memo(VehicleTransferEditCard);
